import { useCallback, useEffect, useState } from 'react'
import { EyeOff, FileSpreadsheet, FileText, Loader2, Tags } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../lib/shadcn/dropdown-menu'
import { toast } from '../../lib/shadcn/sonner'
import { useGetPriceSheet, useGetPriceSheetPhoto, useSetPriceSheetExclusions } from '../../hooks/backend/crm'
import {
  PRICE_SHEET_DISCOUNTS,
  discountLabel,
  downloadBlob,
  loadPriceSheetDiscount,
  priceSheetFileName,
  savePriceSheetDiscount,
  type PriceSheetData,
  type PriceSheetDiscount,
} from '../../utils/priceSheet'
import { buildPriceSheetPdf, loadPriceSheetPhotos } from '../../utils/priceSheetPdf'
import { buildPriceSheetXlsx } from '../../utils/priceSheetXlsx'
import { HelpText } from '../../components/HelpText'

export type PriceSheetKind = 'pdf' | 'xlsx'

export interface PriceSheetFile {
  kind: PriceSheetKind
  fileName: string
  blob: Blob
}

type StoredPhoto = { type: string; data: string }

/** Builds the wholesale price sheet as a PDF or Excel file from live product data. */
export function usePriceSheetFiles() {
  const getPriceSheet = useGetPriceSheet()
  // Four photo downloads at a time, one per hook.
  const photo1 = useGetPriceSheetPhoto()
  const photo2 = useGetPriceSheetPhoto()
  const photo3 = useGetPriceSheetPhoto()
  const photo4 = useGetPriceSheetPhoto()
  const [building, setBuilding] = useState<PriceSheetKind | null>(null)
  // Discount off MSRP for the "Your cost" column, remembered on this browser.
  const [discount, setDiscountState] = useState<PriceSheetDiscount>(loadPriceSheetDiscount)
  const setDiscount = useCallback((next: PriceSheetDiscount) => {
    setDiscountState(next)
    savePriceSheetDiscount(next)
  }, [])

  const build = useCallback(
    async (kind: PriceSheetKind): Promise<PriceSheetFile> => {
      setBuilding(kind)
      try {
        const all = (await getPriceSheet.trigger({}, { skipCache: true })) as PriceSheetData | undefined
        if (!all) throw new Error('Could not load products for the price sheet')
        const data = { ...all, flavors: all.flavors.filter(f => !f.excluded) }
        if (kind === 'xlsx') {
          return { kind, fileName: priceSheetFileName(data, 'xlsx'), blob: buildPriceSheetXlsx(data, discount) }
        }
        const fetchers = [photo1, photo2, photo3, photo4].map(
          hook => async (fileId: string) => ((await hook.trigger({ fileId })) as StoredPhoto | undefined) ?? null
        )
        const photos = await loadPriceSheetPhotos(data.flavors, fetchers)
        return { kind, fileName: priceSheetFileName(data, 'pdf'), blob: buildPriceSheetPdf(data, photos, discount) }
      } finally {
        setBuilding(null)
      }
    },
    [getPriceSheet, photo1, photo2, photo3, photo4, discount]
  )

  return { build, building, discount, setDiscount }
}

/** Menu rows to pick the "Your cost" discount; the menu stays open on pick. */
export function PriceSheetDiscountItems({
  discount,
  onChange,
}: {
  discount: PriceSheetDiscount
  onChange: (discount: PriceSheetDiscount) => void
}) {
  return (
    <>
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        Your cost column (off MSRP)
      </DropdownMenuLabel>
      {PRICE_SHEET_DISCOUNTS.map(d => (
        <DropdownMenuCheckboxItem
          key={d}
          checked={discount === d}
          onSelect={event => event.preventDefault()}
          onCheckedChange={() => onChange(d)}
        >
          {discountLabel(d)} off
        </DropdownMenuCheckboxItem>
      ))}
    </>
  )
}

/** Pick which flavors the price sheet leaves out; saved for everyone. */
function PriceSheetFlavorsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const getPriceSheet = useGetPriceSheet()
  const setExclusions = useSetPriceSheetExclusions()
  const [flavors, setFlavors] = useState<PriceSheetData['flavors']>([])
  const [excluded, setExcluded] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = (await getPriceSheet.trigger({}, { skipCache: true })) as PriceSheetData | undefined
      const list = data?.flavors ?? []
      setFlavors(list)
      setExcluded(new Set(list.filter(f => f.excluded).map(f => f.flavor)))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not load flavors')
    } finally {
      setLoading(false)
    }
  }, [getPriceSheet])

  useEffect(() => {
    if (open) void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const toggle = (flavor: string, include: boolean) => {
    setExcluded(prev => {
      const next = new Set(prev)
      if (include) next.delete(flavor)
      else next.add(flavor)
      return next
    })
  }

  const save = async () => {
    setSaving(true)
    try {
      await setExclusions.trigger({ flavors: [...excluded] })
      toast.success(
        excluded.size === 0
          ? 'Every flavor is on the price sheet'
          : `${excluded.size} flavor${excluded.size === 1 ? '' : 's'} left off the price sheet`
      )
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  // Group by tier, in the order the sheet uses.
  const groups: { tier: string; items: PriceSheetData['flavors'] }[] = []
  for (const f of flavors) {
    const tier = f.tier ?? 'Other'
    let group = groups.find(g => g.tier === tier)
    if (!group) {
      group = { tier, items: [] }
      groups.push(group)
    }
    group.items.push(f)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!saving) onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Price sheet flavors</DialogTitle>
          <DialogDescription><HelpText>
            Uncheck flavors to leave them off the PDF and Excel price sheets, like ones made only for one
            customer. This doesn&apos;t change B2B.
          </HelpText></DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map(group => (
                <div key={group.tier}>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                    {group.tier}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {group.items.map(f => {
                      const id = `price-sheet-flavor-${f.flavor.replace(/[^a-z0-9]+/gi, '-')}`
                      return (
                        <label key={f.flavor} htmlFor={id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                          <Checkbox
                            id={id}
                            checked={!excluded.has(f.flavor)}
                            onCheckedChange={checked => toggle(f.flavor, checked === true)}
                          />
                          <span className={excluded.has(f.flavor) ? 'text-muted-foreground line-through' : ''}>
                            {f.flavor}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="items-center sm:justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {flavors.length - excluded.size} of {flavors.length} flavors on the sheet
          </span>
          <Button onClick={() => void save()} disabled={saving || loading}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** CRM header button: download the price sheet as a PDF or Excel file. */
export default function PriceSheetMenu() {
  const { build, building, discount, setDiscount } = usePriceSheetFiles()
  const [flavorsOpen, setFlavorsOpen] = useState(false)

  const download = async (kind: PriceSheetKind) => {
    try {
      const file = await build(kind)
      downloadBlob(file.blob, file.fileName)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not build the price sheet')
    }
  }

  return (
    <>
      <PriceSheetFlavorsDialog open={flavorsOpen} onOpenChange={setFlavorsOpen} />
      {/* modal={false}: the menu and the flavors dialog use different Radix
          copies, so a modal menu that opens the dialog could leave the page
          with pointer-events: none after the dialog closes. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={building !== null}>
            {building ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tags className="w-4 h-4" />}
            {building ? 'Building…' : 'Price Sheet'}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Active B2B products with MSRP, your cost, tier minimums and seasonal dates
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <PriceSheetDiscountItems discount={discount} onChange={setDiscount} />
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void download('pdf')}>
            <FileText className="w-4 h-4" /> Download PDF catalog
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void download('xlsx')}>
            <FileSpreadsheet className="w-4 h-4" /> Download Excel sheet
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setFlavorsOpen(true)}>
            <EyeOff className="w-4 h-4" /> Choose flavors…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
